/**
 * @file    useProgramCatalog.ts
 * @scope   CPR
 * @summary Real data source of the CPR ProgramsManager: program options, questions per program
 *          and filter fields per program.
 * @consumes Drawer `availableCollections` (GuardRails `dataCollectionsList`), GuardRails GetData
 *           (`partitionGuid=<dataCollectionGuid>`) via useAxios
 * @produces ProgramCatalog
 * @sideEffects One GetData request per selected program (sequential, cached by the axios layer).
 * @notes Programs are the workspace data collections (docs/guardrails.md: contributors are
 *        `DataCollectionGuid` + `OriginId` peers). Questions/filters come from the same GetData
 *        call the Enterprise FieldsManager uses (`config.fields` / `config.filters.advancedFilters`).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAxios } from 'hooks/useAxios';
import { useStore } from 'store/useGlobalStore';
import { getChartTypeData } from 'services/Torogoz/TogorozApi';
import { AdvancedFilterFieldConfig } from 'models/WidgetModel';
import {
  buildEndpointWithPartition,
  buildFieldsManagerViewOptions,
  mapFieldsToSelectOptions,
} from '../../../Enterprise/utils/mapping/fieldMapping';
import { ProgramOption, QuestionOption } from './programsManagerModel';

/** Per-program options resolved from GetData. */
interface ProgramCatalogEntry {
  questions: QuestionOption[];
  filterFields: AdvancedFilterFieldConfig[];
}

export interface ProgramCatalog {
  programOptions: ProgramOption[];
  getQuestionOptions: (programGuid?: string) => QuestionOption[];
  getFilterFields: (programGuid?: string) => AdvancedFilterFieldConfig[];
  /** Filter fields for the rollup-level partition filters. */
  partitionFilterFields: AdvancedFilterFieldConfig[];
  isLoading: boolean;
  /** True while the questions/filters of `programGuid` have not been loaded yet. */
  isProgramLoading?: (programGuid?: string) => boolean;
}

const EMPTY_FILTERS: AdvancedFilterFieldConfig[] = [];
const EMPTY_QUESTIONS: QuestionOption[] = [];

/**
 * Questions of a program. WHY two sources: the CPR GetData payload returns `fields: []` and the
 * questions under `questions` (`qName` / `shortText`); Medusa payloads use `fields`.
 */
const mapQuestions = (config: any, fieldsOptions: any[]): QuestionOption[] => {
  if (fieldsOptions.length) return mapFieldsToSelectOptions(fieldsOptions);
  return (config?.questions ?? []).map((q: any) => ({
    value: q.qName,
    label: q.shortText || q.longText || q.qName,
  }));
};

/**
 * @param endpoint GuardRails GetData URL of the current chart (same one Enterprise uses).
 * @param collections Drawer `availableCollections` (`{ value, label, originId }`).
 * @param programGuids Data collections currently selected in the rollup.
 */
export const useProgramCatalog = (
  endpoint: string | undefined,
  collections: any[] | undefined,
  programGuids: string[]
): ProgramCatalog => {
  const isConsumer = useStore((state) => state.isConsumer);
  const { response, axiosFetch, loading } = useAxios();
  const [entries, setEntries] = useState<Record<string, ProgramCatalogEntry>>({});
  const inFlightGuid = useRef<string | null>(null);

  const programOptions = useMemo<ProgramOption[]>(
    () =>
      (collections ?? []).map((c) => ({
        value: c.value,
        label: c.label,
        originId: c.originId ?? '',
      })),
    [collections]
  );

  // WHY sequential: `useAxios` exposes one response at a time, so requests are chained.
  const nextGuid = programGuids.find((guid) => guid && !entries[guid]);

  useEffect(() => {
    if (!endpoint || !nextGuid || inFlightGuid.current) return;
    inFlightGuid.current = nextGuid;
    // TODO(rubencv): TEMP CPR DEBUG — remove.
    // eslint-disable-next-line no-console
    console.log('[CPR] questions request', buildEndpointWithPartition(endpoint, nextGuid));
    getChartTypeData(buildEndpointWithPartition(endpoint, nextGuid), axiosFetch, isConsumer);
  }, [endpoint, nextGuid]);

  useEffect(() => {
    const guid = inFlightGuid.current;
    // eslint-disable-next-line no-console
    console.log(
      '[CPR] questions response',
      guid,
      response?.config && {
        fields: response.config.fields?.length,
        questions: response.config.questions?.length,
        sampleField: response.config.fields?.[0],
      }
    );
    if (!response || !guid) return;
    const options = buildFieldsManagerViewOptions(response?.config, { value: guid } as any);
    setEntries((prev) => ({
      ...prev,
      [guid]: {
        questions: mapQuestions(response?.config, options.FieldsOptions),
        filterFields: options.FiltersOptions as AdvancedFilterFieldConfig[],
      },
    }));
    inFlightGuid.current = null;
  }, [response]);

  const getQuestionOptions = useCallback(
    (programGuid?: string) => (programGuid && entries[programGuid]?.questions) || EMPTY_QUESTIONS,
    [entries]
  );

  const getFilterFields = useCallback(
    (programGuid?: string) => (programGuid && entries[programGuid]?.filterFields) || EMPTY_FILTERS,
    [entries]
  );

  const isProgramLoading = useCallback(
    (programGuid?: string) => Boolean(endpoint && programGuid && !entries[programGuid]),
    [endpoint, entries]
  );

  // TODO(rubencv): confirm with backend which fields apply to rollup-level partition filters.
  // Assumption: the first loaded program's filter fields.
  const partitionFilterFields = useMemo(() => {
    const loaded = programGuids.find((guid) => entries[guid]);
    return loaded ? entries[loaded].filterFields : EMPTY_FILTERS;
  }, [programGuids, entries]);

  return {
    programOptions,
    getQuestionOptions,
    getFilterFields,
    partitionFilterFields,
    isLoading: loading,
    isProgramLoading,
  };
};
